const REDACTED = "[REDACTED]";

/**
 * PII scalar fields — always redacted regardless of where they appear in the state tree.
 */
const ALWAYS_REDACT_FIELDS = new Set([
  "IDNumber",
  "IDCardNumber",
  "IDExpiryDate",
  "password",
  "apiKey",
  "supplierName",
  "supplierABN",
  "supplierAddress",
  "supplierEmail",
  "supplierContactName",
  "supplierPhone",
  "maritalStatus",
  "residencyStatus",
  "visaType",
  "visaExpiryDate",
  "livingArrangements",
]);

/**
 * PII object/array fields — the entire value is replaced with REDACTED.
 */
const ALWAYS_REDACT_OBJECT_FIELDS = new Set([
  "phoneNumbers",
  "bankDetails",
  "creditProfile",
  "lenderSettings",
  "income",
  "expenses",
  "liabilities",
  "customerAssets",
  "employers",
  "employmentIncomeVerification",
  "directors",
  "shareHolders",
  "beneficiaries",
]);

/**
 * Fields redacted only inside the `user` slice (top-level user state).
 */
const USER_SLICE_REDACT_FIELDS = new Set(["email"]);

/**
 * Fields redacted inside customer objects.
 */
const CUSTOMER_REDACT_FIELDS = new Set(["customerTitle"]);

/**
 * Recursively walk an object and redact PII fields.
 * Returns a new object — never mutates the original.
 */
function redactPII(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value !== "object") return value;

  if (Array.isArray(value)) {
    return value.map((item) => redactPII(item));
  }

  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    if (ALWAYS_REDACT_FIELDS.has(key)) {
      result[key] = val !== undefined && val !== null ? REDACTED : val;
    } else if (ALWAYS_REDACT_OBJECT_FIELDS.has(key)) {
      result[key] = val !== undefined && val !== null ? REDACTED : val;
    } else {
      result[key] = redactPII(val);
    }
  }
  return result;
}

/**
 * Redact PII specific to the `user` slice (broker/platform user).
 * Keeps firstName, lastName. Redacts email, lenderSettings, etc.
 */
function redactUserSlice(userState: unknown): unknown {
  if (!userState || typeof userState !== "object") return userState;

  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(userState as Record<string, unknown>)) {
    if (USER_SLICE_REDACT_FIELDS.has(key)) {
      result[key] = val !== undefined && val !== null ? REDACTED : val;
    } else if (ALWAYS_REDACT_FIELDS.has(key)) {
      result[key] = val !== undefined && val !== null ? REDACTED : val;
    } else if (ALWAYS_REDACT_OBJECT_FIELDS.has(key)) {
      result[key] = val !== undefined && val !== null ? REDACTED : val;
    } else {
      result[key] = redactPII(val);
    }
  }
  return result;
}

/**
 * Redact PII from a customer object.
 * Keeps: firstName, lastName, dateOfBirth, emails, addresses.
 * Redacts: IDNumber, IDCardNumber, IDExpiryDate, phoneNumbers, bankDetails, etc.
 */
function redactCustomer(customer: unknown): unknown {
  if (!customer || typeof customer !== "object") return customer;

  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(customer as Record<string, unknown>)) {
    if (CUSTOMER_REDACT_FIELDS.has(key)) {
      result[key] = val !== undefined && val !== null ? REDACTED : val;
    } else if (ALWAYS_REDACT_FIELDS.has(key)) {
      result[key] = val !== undefined && val !== null ? REDACTED : val;
    } else if (ALWAYS_REDACT_OBJECT_FIELDS.has(key)) {
      result[key] = val !== undefined && val !== null ? REDACTED : val;
    } else {
      // Recurse for nested objects (e.g. customerProfile) but keep emails, addresses, firstName, etc.
      result[key] = typeof val === "object" && val !== null ? redactPII(val) : val;
    }
  }
  return result;
}

/**
 * Redact PII from an entity object.
 * Keeps: emails, addresses, entityName, abn, acn.
 * Redacts: phoneNumbers, directors, shareHolders, beneficiaries.
 */
function redactEntity(entity: unknown): unknown {
  if (!entity || typeof entity !== "object") return entity;

  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(entity as Record<string, unknown>)) {
    if (ALWAYS_REDACT_FIELDS.has(key)) {
      result[key] = val !== undefined && val !== null ? REDACTED : val;
    } else if (ALWAYS_REDACT_OBJECT_FIELDS.has(key)) {
      result[key] = val !== undefined && val !== null ? REDACTED : val;
    } else {
      result[key] = typeof val === "object" && val !== null ? redactPII(val) : val;
    }
  }
  return result;
}

/**
 * Redact supplier PII from an asset object.
 */
function redactAsset(asset: unknown): unknown {
  if (!asset || typeof asset !== "object") return asset;

  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(asset as Record<string, unknown>)) {
    if (ALWAYS_REDACT_FIELDS.has(key)) {
      result[key] = val !== undefined && val !== null ? REDACTED : val;
    } else {
      result[key] = val;
    }
  }
  return result;
}

/**
 * Redact PII from the userDetails (applicationForm) slice.
 */
function redactUserDetailsSlice(state: unknown): unknown {
  if (!state || typeof state !== "object") return state;

  const userDetails = state as Record<string, unknown>;
  const result = { ...userDetails };

  if (userDetails.application && typeof userDetails.application === "object") {
    const app = userDetails.application as Record<string, unknown>;
    const redactedApp = { ...app };

    // Redact customers
    if (Array.isArray(app.customers)) {
      redactedApp.customers = app.customers.map(redactCustomer);
    }

    // Redact entities
    if (Array.isArray(app.entities)) {
      redactedApp.entities = app.entities.map(redactEntity);
    }

    // Redact asset supplier fields
    if (app.asset && typeof app.asset === "object") {
      redactedApp.asset = redactAsset(app.asset);
    }

    result.application = redactedApp;
  }

  return result;
}

/**
 * Redact PII from the users slice.
 * The slice contains a `users.docs` array of user objects (from getAllUsers),
 * plus top-level fields merged from various API responses (login, register, etc.).
 */
function redactUsersSlice(state: unknown): unknown {
  if (!state || typeof state !== "object") return state;

  const usersState = state as Record<string, unknown>;
  const result = { ...usersState };

  // Redact the docs array (list of all users)
  if (usersState.users && typeof usersState.users === "object") {
    const usersObj = usersState.users as Record<string, unknown>;
    const redactedUsersObj = { ...usersObj };

    if (Array.isArray(usersObj.docs)) {
      redactedUsersObj.docs = usersObj.docs.map(redactUserSlice);
    }

    result.users = redactedUsersObj;
  }

  // Redact top-level fields that get merged from API responses.
  for (const key of Object.keys(result)) {
    if (key === "users") continue; // already handled above
    if (USER_SLICE_REDACT_FIELDS.has(key)) {
      result[key] = result[key] !== undefined && result[key] !== null ? REDACTED : result[key];
    } else if (ALWAYS_REDACT_FIELDS.has(key)) {
      result[key] = result[key] !== undefined && result[key] !== null ? REDACTED : result[key];
    } else if (ALWAYS_REDACT_OBJECT_FIELDS.has(key)) {
      result[key] = result[key] !== undefined && result[key] !== null ? REDACTED : result[key];
    }
  }

  return result;
}

/**
 * Sanitise the full Redux state tree before sending to Sentry.
 * Non-PII slices are passed through as-is for debugging value.
 */
function sanitiseStateForSentry(state: Record<string, unknown>): Record<string, unknown> {
  return {
    ...state,
    userDetails: redactUserDetailsSlice(state.userDetails),
    user: redactUserSlice(state.user),
    users: redactUsersSlice(state.users),
    customer: state.customer && typeof state.customer === "object"
      ? { ...(state.customer as Record<string, unknown>), contactData: redactCustomer((state.customer as Record<string, unknown>).contactData) }
      : state.customer,
  };
}

export { sanitiseStateForSentry, redactPII, REDACTED };
